import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { reload, update } from '../../app/shell.js';
import { bar, empty, pageHead, seg, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const TYPES = ['All', 'Job', 'Internship', 'Project'];
const START = ['Immediately', 'In 2 weeks', 'In a month'];

function action(o) {
  if (o.interest === 'shortlisted') return html`<div class="mt-s">${tag('Shortlisted, chat unlocked', 'ok')}</div><a class="btn sm mt-s" href="/student/messages">Open messages</a>`;
  if (o.interest === 'declined') return html`<div class="mt-s">${tag('Not this time', 'n')}</div>`;
  if (o.interest) return html`<div class="mt-s">${tag('Interest sent, Vignova is reviewing', 'wn')}</div>`;
  if (!o.eligibility.eligible) {
    return html`<p class="sm mt-s">Missing: ${o.eligibility.missing.join(', ')}.</p><a class="btn ghost sm" href="/student/learn">Find a course</a>`;
  }
  return html`<button type="button" class="btn sm mt-s" data-act="opp.interest" data-id="${o.id}">Express interest</button>`;
}

function recommended(list) {
  if (!list?.length) return '';
  return html`<h3>Recommended to companies for you</h3>
    <p class="note">Vignova checked these roles against your skills and recommended you. The company sees your profile. Your phone and email stay private until the company shortlists you.</p>
    <div class="grid g2 mb">${list.map((r) => html`<div class="card"><div class="row between">${tag(r.type)}${r.stage ? tag(r.stage, 'ok') : tag('Recommended', 'wn')}</div>
      <h3 class="mt-s">${r.title}</h3>
      <p class="sm"><b>${r.company.name}</b>${r.company.verified ? ' ' : ''}${r.company.verified ? tag('Verified', 'ok') : ''}<br><span class="mu">${[r.company.industry, r.company.city].filter(Boolean).join(' · ')}</span></p>
      ${r.company.about ? html`<p class="sm">${r.company.about}</p>` : ''}
      ${r.company.website ? html`<p class="sm"><a href="${r.company.website}" target="_blank" rel="noopener noreferrer">${r.company.website}</a></p>` : ''}
      <p class="sm mu">${[r.location, r.work_mode, r.experience, r.budget_text, r.start_by && `Start ${r.start_by}`].filter(Boolean).join(' · ')}</p>
      ${r.description ? html`<p class="sm">${r.description}</p>` : ''}
      <div class="chips">${r.skills.map((k) => tag(k, r.skills_you_have.includes(k) ? 'ok' : 'n'))}</div>
      ${r.conversation_id ? html`<a class="btn sm mt-s" href="/student/messages">Open messages</a>` : html`<p class="note mt-s">If the company is interested, it will shortlist you and a chat opens here.</p>`}</div>`)}</div>`;
}

const opportunities = {
  path: '/student/opportunities',
  roles: ['student'],
  title: 'Opportunities',
  ui: () => ({ type: 'All' }),
  async load(ctx) {
    const [d, rec] = await Promise.all([
      api.get('/student/opportunities', { signal: ctx.signal }),
      api.get('/student/recommendations', { signal: ctx.signal }).catch(() => ({ recommendations: [] })),
    ]);
    return { ...d, recommendations: rec.recommendations };
  },
  view(d, ui) {
    const list = d.opportunities.filter((o) => ui.type === 'All' || o.type === ui.type);
    return html`${pageHead('Opportunities for you', 'Private to your account. Matched to the skills you have proven.', seg(TYPES, ui.type, 'opp.type', 'Type'))}
      ${recommended(d.recommendations)}
      ${list.length ? html`<div class="grid g3">${list.map((o) => html`<div class="card"><div class="row between">${tag(o.type)}${o.eligibility.eligible ? tag('Eligible', 'ok') : tag('Not yet eligible', 'wn')}</div>
        <h3 class="mt-s">${o.title}</h3><p class="sm mu">${o.org_name}${o.location ? `, ${o.location}` : ''}${o.pay_text ? `, ${o.pay_text}` : ''}</p>
        <div class="chips">${o.skills.map((k) => tag(k, o.eligibility.missing.includes(k) ? 'n' : 'ok'))}</div>
        <div class="row mt-s nw"><div class="f1">${bar(o.eligibility.percent, o.eligibility.eligible ? 'ok' : '', 'Match')}</div><b class="sm">${o.eligibility.percent}% match</b></div>${action(o)}</div>`)}</div>`
        : empty('brief', 'Nothing here yet', 'New roles are added by Vignova. Check back soon.')}`;
  },
};

register({
  'opp.type': (el) => update((ui) => { ui.type = el.dataset.value; }),
  'opp.interest': (el) => {
    openModal({
      title: 'Express interest',
      body: html`<p>You are telling Vignova you want to be considered. Vignova reviews, and if it fits, recommends your profile to the company.</p>
        <form id="interest-form" data-submit="opp.send" data-id="${el.dataset.id}" novalidate>${field('in-note', 'Why you are a good fit', { name: 'note', rows: 3, req: false, ph: 'Optional, 2 to 3 lines', max: 1000 })}
        ${field('in-when', 'When can you start?', { name: 'start_when', opts: START })}</form><p class="note">Your profile is shared only with this company, and only if Vignova recommends you.</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="interest-form" class="btn">Send interest</button>`,
    });
  },
  'opp.send': (form) => submitForm(form, async (values) => {
    await api.post(`/student/opportunities/${form.dataset.id}/interest`, { body: values });
    closeModal(false);
    toast('Interest sent to Vignova', 'send');
    await reload();
  }),
});

export const routes = [opportunities];
